import { useEffect, useRef, useState } from "react";
import { Plus, Search, Tag, X } from "lucide-react";
import { EVENT_TYPES, EVENT_TYPE_GROUPS, getEventType, searchEventTypes } from "@shared/eventTypes";
import { SheetModal } from "@/components/SheetModal";
import { Pill } from "@/components/ux/Pill";

interface Props {
  value: string[];
  onChange: (tags: string[]) => void;
  max?: number;
  placeholder?: string;
  label?: string;
}

export function TagSelector({
  value,
  onChange,
  max = 10,
  placeholder = "Add tags…",
  label,
}: Props) {
  const [open, setOpen] = useState(false);

  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-xs text-neutral-500">
            {value.length}/{max}
          </span>
        </div>
      )}

      <div
        className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 rounded-xl border border-base-300 bg-base-100 cursor-pointer hover:border-primary-400 transition-colors"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setOpen(true)}
        aria-label="Edit tags"
      >
        {value.length === 0 ? (
          <span className="flex items-center gap-1.5 text-sm text-neutral-400 px-1">
            <Tag className="w-3.5 h-3.5" />
            {placeholder}
          </span>
        ) : (
          value.map((tag) => {
            const known = getEventType(tag);
            return (
              <Pill
                key={tag}
                variant="soft"
                size="sm"
                leading={known ? <span className="text-sm">{known.emoji}</span> : undefined}
                trailing={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(tag);
                    }}
                    className="ml-0.5 rounded-full hover:bg-primary-500/20 p-0.5 transition-colors"
                    aria-label={`Remove ${tag}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                }
              >
                {known ? known.label : tag}
              </Pill>
            );
          })
        )}
        {value.length < max && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-neutral-400 hover:text-primary-500 hover:bg-base-200 transition-colors"
            aria-label="Add tag"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>

      <TagSelectorModal
        isOpen={open}
        onClose={() => setOpen(false)}
        value={value}
        onChange={onChange}
        max={max}
      />
    </div>
  );
}

function TypeChips({
  types,
  draft,
  atLimit,
  onToggle,
}: {
  types: readonly { slug: string; emoji: string; label: string }[];
  draft: string[];
  atLimit: boolean;
  onToggle: (slug: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {types.map((t) => {
        const isOn = draft.includes(t.slug);
        const disabled = !isOn && atLimit;
        return (
          <Pill
            key={t.slug}
            variant={isOn ? "solid" : "outline"}
            disabled={disabled}
            onClick={() => onToggle(t.slug)}
            aria-pressed={isOn}
            leading={<span className="text-base">{t.emoji}</span>}
          >
            {t.label}
          </Pill>
        );
      })}
    </div>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string[];
  onChange: (tags: string[]) => void;
  max: number;
}

function TagSelectorModal({ isOpen, onClose, value, onChange, max }: ModalProps) {
  const [draft, setDraft] = useState<string[]>(value);
  const [query, setQuery] = useState("");
  const [customInput, setCustomInput] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const customRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(value);
      setQuery("");
      setCustomInput("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const atLimit = draft.length >= max;
  const q = query.trim();
  const filteredTypes = q ? searchEventTypes(q) : EVENT_TYPES;
  const customTags = draft.filter((t) => !getEventType(t));

  const toggleKnown = (slug: string) => {
    if (draft.includes(slug)) setDraft(draft.filter((s) => s !== slug));
    else if (!atLimit) setDraft([...draft, slug]);
  };

  const addCustom = () => {
    const t = customInput.trim();
    if (!t || draft.includes(t) || atLimit) return;
    setDraft((prev) => [...prev, t]);
    setCustomInput("");
    customRef.current?.focus();
  };

  const removeTag = (tag: string) => setDraft((prev) => prev.filter((t) => t !== tag));

  const handleDone = () => { onChange(draft); onClose(); };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg h-[88vh] sm:h-[80vh]">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">Tags</h2>
        <span className="text-sm text-neutral-400 dark:text-neutral-500">
          {draft.length}/{max}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search event types…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-0 outline-none text-sm placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500/40"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 min-h-0">
        {filteredTypes.length === 0 ? (
          <p className="text-sm text-neutral-400 dark:text-neutral-500 py-4 text-center">
            No results for &ldquo;{query}&rdquo;
          </p>
        ) : q ? (
          <TypeChips types={filteredTypes} draft={draft} atLimit={atLimit} onToggle={toggleKnown} />
        ) : (
          EVENT_TYPE_GROUPS.map((g) => {
            const items = filteredTypes.filter((t) => t.group === g.slug);
            if (items.length === 0) return null;
            return (
              <div key={g.slug}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-2.5">
                  {g.label}
                </p>
                <TypeChips types={items} draft={draft} atLimit={atLimit} onToggle={toggleKnown} />
              </div>
            );
          })
        )}

        {!query && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-2.5">
              Custom tags
            </p>
            {customTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {customTags.map((t) => (
                  <Pill
                    key={t}
                    variant="soft"
                    trailing={
                      <button
                        type="button"
                        onClick={() => removeTag(t)}
                        className="ml-0.5 hover:opacity-70"
                        aria-label={`Remove ${t}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    }
                  >
                    {t}
                  </Pill>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                ref={customRef}
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                }}
                placeholder="Add a custom tag…"
                maxLength={50}
                disabled={atLimit}
                className="w-full pl-3 pr-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-0 outline-none text-sm placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500/40 disabled:opacity-40"
              />
              <button
                type="button"
                onClick={addCustom}
                disabled={!customInput.trim() || atLimit}
                className="btn btn-sm btn-ghost shrink-0"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-5 pt-3 flex gap-2 border-t border-neutral-200 dark:border-neutral-800 shrink-0">
        {draft.length > 0 && (
          <button type="button" onClick={() => setDraft([])} className="btn btn-secondary">
            Clear
          </button>
        )}
        <button type="button" onClick={handleDone} className="btn btn-primary flex-1">
          Done
        </button>
      </div>
    </SheetModal>
  );
}
